/**
 * SecondScreen - Multi-panel view for second monitor
 *
 * Displays Ask Bot and AI Analysis panels side by side with actions bar.
 * Light mode only design.
 */

import { useState, useRef, useEffect } from 'react'
import {
  Bot,
  Sparkles,
  RefreshCw,
  Send,
  Loader2,
  Trash2,
  Copy,
  CheckCircle,
  Crown,
  Wand2,
  Clock,
  FileText,
  AlertTriangle,
  X,
  TrendingUp,
  Lightbulb,
  BookOpen,
  Search,
  ArrowUpCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useLiteContext } from '@/hooks/useLiteContext'
import {
  analyzeTicket,
  sendChatMessage,
  addNote,
  addTimeEntry,
  generateResponse,
} from '@/services/api'
import type { AnalysisResult, ChatMessage } from '@/types/halopsa'

// Metric configuration
type MetricKey = 'clarity' | 'sentiment' | 'empathy' | 'acumen' | 'cadence' | 'disruption' | 'age' | 'priority' | 'chronic' | 'skills' | 'vip' | 'responseQuality'

interface MetricConfig {
  key: MetricKey
  label: string
  invertColor?: boolean
}

const METRICS: MetricConfig[] = [
  { key: 'clarity', label: 'CLARITY' },
  { key: 'sentiment', label: 'SENTIMENT' },
  { key: 'empathy', label: 'EMPATHY' },
  { key: 'acumen', label: 'ACUMEN', invertColor: true },
  { key: 'cadence', label: 'CADENCE' },
  { key: 'disruption', label: 'DISRUPTION', invertColor: true },
  { key: 'age', label: 'AGE', invertColor: true },
  { key: 'priority', label: 'PRIORITY', invertColor: true },
  { key: 'chronic', label: 'CHRONIC', invertColor: true },
  { key: 'skills', label: 'SKILLS', invertColor: true },
  { key: 'vip', label: 'VIP' },
  { key: 'responseQuality', label: 'RESPONSE' },
]

function getScoreColor(score: number, invert: boolean = false): string {
  const effectiveScore = invert ? 11 - score : score
  if (effectiveScore >= 7) return 'bg-emerald-50 border-emerald-300'
  if (effectiveScore >= 4) return 'bg-amber-50 border-amber-300'
  return 'bg-red-50 border-red-300'
}

function getScoreTextColor(score: number, invert: boolean = false): string {
  const effectiveScore = invert ? 11 - score : score
  if (effectiveScore >= 7) return 'text-emerald-500'
  if (effectiveScore >= 4) return 'text-amber-500'
  return 'text-red-500'
}

// Loading messages
const LOADING_MESSAGES = [
  "Analyzing your request...",
  "Searching the knowledge base...",
  "Hang on a moment more...",
  "Consulting the AI brain...",
  "Digging through the archives...",
  "Finding the best answer...",
]

export function SecondScreen() {
  // Context
  const { context, loading, error, refetch } = useLiteContext()

  // Analysis state
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null)
  const [analysisLoading, setAnalysisLoading] = useState(false)

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [chatError, setChatError] = useState<string | null>(null)
  const chatEndRef = useRef<HTMLDivElement>(null)

  // Actions state
  const [noteInput, setNoteInput] = useState('')
  const [noteType, setNoteType] = useState<'private' | 'public'>('private')
  const [noteLoading, setNoteLoading] = useState(false)
  const [noteSuccess, setNoteSuccess] = useState(false)

  // Time entry state
  const [timeMinutes, setTimeMinutes] = useState(15)
  const [timeLoading, setTimeLoading] = useState(false)
  const [timeSuccess, setTimeSuccess] = useState(false)

  // Response generator state
  const [responseTone, setResponseTone] = useState<'professional' | 'friendly' | 'technical'>('professional')
  const [generatedResponse, setGeneratedResponse] = useState('')
  const [responseLoading, setResponseLoading] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)

  // Screen pop alerts
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set())

  // Loading message rotation
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0)

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null
    if (chatLoading || analysisLoading) {
      interval = setInterval(() => {
        setLoadingMsgIndex(prev => (prev + 1) % LOADING_MESSAGES.length)
      }, 2500)
    }
    return () => { if (interval) clearInterval(interval) }
  }, [chatLoading, analysisLoading])

  // Reset alerts when ticket changes
  useEffect(() => {
    if (context?.ticket.id) {
      setDismissedAlerts(new Set())
    }
  }, [context?.ticket.id])

  // Auto-analyze on load
  useEffect(() => {
    if (context && !analysis && !analysisLoading) {
      handleAnalyze()
    }
  }, [context])

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  // Handlers
  const handleAnalyze = async () => {
    if (!context?.ticket.id) return
    setAnalysisLoading(true)
    try {
      const result = await analyzeTicket(context.ticket.id)
      setAnalysis(result)
    } catch (err: unknown) {
      console.error('Analysis failed:', err)
    } finally {
      setAnalysisLoading(false)
    }
  }

  const handleSendChat = async () => {
    if (!context?.ticket.id || !chatInput.trim()) return
    const userMessage = chatInput.trim()
    setChatInput('')
    setChatLoading(true)
    setChatError(null)

    const newUserMsg: ChatMessage = { role: 'user', content: userMessage }
    setChatMessages(prev => [...prev, newUserMsg])

    try {
      const result = await sendChatMessage(context.ticket.id, userMessage, chatMessages)
      const responseText = result.response || 'I apologize, but I was unable to generate a response. Please try again.'
      const assistantMsg: ChatMessage = { role: 'assistant', content: responseText }
      setChatMessages(prev => [...prev, assistantMsg])
    } catch (err: unknown) {
      console.error('[Chat] Error:', err)
      setChatError(err instanceof Error ? err.message : 'Failed to send message')
    } finally {
      setChatLoading(false)
    }
  }

  const handleSubmitNote = async () => {
    if (!context?.ticket.id || !noteInput.trim()) return
    setNoteLoading(true)
    try {
      await addNote(context.ticket.id, noteInput.trim(), noteType === 'private' ? 'internal' : 'public')
      setNoteInput('')
      setNoteSuccess(true)
      setTimeout(() => setNoteSuccess(false), 2000)
    } catch (err) {
      console.error('Note failed:', err)
    } finally {
      setNoteLoading(false)
    }
  }

  const handleLogTime = async () => {
    if (!context?.ticket.id || timeMinutes === 0) return
    setTimeLoading(true)
    try {
      const hours = Math.floor(timeMinutes / 60)
      const mins = timeMinutes % 60
      await addTimeEntry(context.ticket.id, hours, mins)
      setTimeSuccess(true)
      setTimeMinutes(0)
      setTimeout(() => setTimeSuccess(false), 2000)
    } catch (err) {
      console.error('Time entry failed:', err)
    } finally {
      setTimeLoading(false)
    }
  }

  const handleGenerateResponse = async () => {
    if (!context?.ticket.id) return
    setResponseLoading(true)
    try {
      const result = await generateResponse(context.ticket.id, responseTone, 'concise')
      setGeneratedResponse(result.response)
    } catch (err) {
      console.error('Response generation failed:', err)
    } finally {
      setResponseLoading(false)
    }
  }

  const handleCopyResponse = () => {
    navigator.clipboard.writeText(generatedResponse)
    setCopySuccess(true)
    setTimeout(() => setCopySuccess(false), 2000)
  }

  const handleUseAsNote = () => {
    setNoteInput(generatedResponse)
    setGeneratedResponse('')
  }

  // Loading state
  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-sm text-gray-600">Loading ticket data...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !context) {
    return (
      <div className="h-screen w-screen flex items-center justify-center p-4 bg-gray-100">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-2 text-gray-900">Error Loading Ticket</h2>
          <p className="text-sm mb-4 text-gray-600">{error || 'No ticket data'}</p>
          <Button onClick={refetch}>Retry</Button>
        </div>
      </div>
    )
  }

  const { ticket, contact } = context

  return (
    <div className="h-screen w-screen overflow-hidden p-4 bg-gray-100">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <span className="text-primary">HALOPOD</span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-900">#{ticket.id}</span>
          </h1>
          <span className="text-sm truncate max-w-md text-gray-500">
            {ticket.summary}
          </span>
        </div>
        <Button variant="ghost" size="icon" onClick={refetch} className="h-8 w-8">
          <RefreshCw className="h-4 w-4 text-gray-600" />
        </Button>
      </div>

      {/* Screen Pop Alerts */}
      {analysis && (
        <div className="mb-3 space-y-2">
          {/* VIP Alert */}
          {contact?.isVip && !dismissedAlerts.has('vip') && (
            <div className="flex items-center justify-between px-4 py-3 rounded-lg border-2 animate-pulse bg-amber-100 border-amber-500">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-amber-500">
                  <Crown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-bold text-amber-800">VIP / Executive Contact</div>
                  <div className="text-sm text-amber-700">{contact.name}{contact.jobTitle ? ` - ${contact.jobTitle}` : ''}</div>
                </div>
              </div>
              <button onClick={() => setDismissedAlerts(prev => new Set([...prev, 'vip']))} className="p-1 rounded hover:bg-amber-500/20 text-amber-600">
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Escalation Alert */}
          {analysis.sentiment?.escalation?.recommended && !dismissedAlerts.has('escalation') && (
            <div className="flex items-center justify-between px-4 py-3 rounded-lg border-2 animate-pulse bg-red-100 border-red-500">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-500">
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-bold text-red-800">Escalation Recommended</div>
                  <div className="text-sm text-red-700">{analysis.sentiment.escalation.factors?.[0] || 'This ticket may require escalation'}</div>
                </div>
              </div>
              <button onClick={() => setDismissedAlerts(prev => new Set([...prev, 'escalation']))} className="p-1 rounded hover:bg-red-500/20 text-red-600">
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col h-[calc(100vh-80px)] gap-3">
        {/* Top Row: Ask Bot + Analysis side by side */}
        <div className="flex-1 grid grid-cols-2 gap-3 min-h-0">
          {/* Ask Bot Panel */}
          <div className="relative rounded-lg shadow-lg border overflow-hidden flex flex-col bg-white border-gray-200">
            <div className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50 border-gray-200">
              <Bot className="h-5 w-5 text-primary" />
              <span className="font-medium text-gray-800">Ask Bot</span>
              <Button variant="ghost" size="icon" onClick={() => setChatMessages([])} disabled={chatMessages.length === 0} className="ml-auto h-7 w-7">
                <Trash2 className="h-4 w-4 text-gray-500" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.length === 0 && (
                <div className="text-center py-8">
                  <Bot className="h-16 w-16 mx-auto mb-4 text-primary/50" />
                  <p className="text-base mb-6 text-gray-500">Ask me anything about this ticket!</p>
                  <div className="grid grid-cols-3 gap-2 max-w-xl mx-auto">
                    <button onClick={() => setChatInput('What is the quickest way to resolve this issue?')}
                      className="text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 bg-white border-gray-200 hover:border-primary/50 hover:bg-primary/5 text-gray-700">
                      <Lightbulb className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>Quick resolution?</span>
                    </button>
                    <button onClick={() => setChatInput('Find documentation or knowledge base articles for this issue')}
                      className="text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 bg-white border-gray-200 hover:border-blue-500/50 hover:bg-blue-50 text-gray-700">
                      <BookOpen className="h-5 w-5 text-blue-500 flex-shrink-0" />
                      <span>Find KB articles</span>
                    </button>
                    <button onClick={() => setChatInput('Find similar tickets and how they were resolved')}
                      className="text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 bg-white border-gray-200 hover:border-purple-500/50 hover:bg-purple-50 text-gray-700">
                      <Copy className="h-5 w-5 text-purple-500 flex-shrink-0" />
                      <span>Similar tickets</span>
                    </button>
                    <button onClick={() => setChatInput('What are the possible root causes of this issue?')}
                      className="text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 bg-white border-gray-200 hover:border-amber-500/50 hover:bg-amber-50 text-gray-700">
                      <Search className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      <span>Root causes?</span>
                    </button>
                    <button onClick={() => setChatInput('Are there any runbooks or procedures for this type of issue?')}
                      className="text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 bg-white border-gray-200 hover:border-cyan-500/50 hover:bg-cyan-50 text-gray-700">
                      <FileText className="h-5 w-5 text-cyan-500 flex-shrink-0" />
                      <span>Find runbooks</span>
                    </button>
                    <button onClick={() => setChatInput('Who should I escalate this issue to?')}
                      className="text-left text-sm rounded-lg px-4 py-3 border transition-colors flex items-center gap-3 bg-white border-gray-200 hover:border-rose-500/50 hover:bg-rose-50 text-gray-700">
                      <ArrowUpCircle className="h-5 w-5 text-rose-500 flex-shrink-0" />
                      <span>Escalation path?</span>
                    </button>
                  </div>
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-4 rounded-lg ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-gray-100 text-gray-800'}`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-1 mb-2 text-primary font-medium text-xs">
                        <Bot className="h-3 w-3" /> AI Assistant
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="p-4 rounded-lg bg-gray-100">
                    <div className="flex items-center gap-3">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <span className="text-sm animate-pulse text-gray-600">
                        {LOADING_MESSAGES[loadingMsgIndex]}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {chatError && (
                <div className="flex justify-start">
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-sm text-red-600">{chatError}</p>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            {/* Input */}
            <div className="p-3 border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendChat()}
                  placeholder="Ask a question about this ticket..."
                  className="flex-1 px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  disabled={chatLoading}
                />
                <Button onClick={handleSendChat} disabled={chatLoading || !chatInput.trim()} className="px-4">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Analysis Panel */}
          <div className="relative rounded-lg shadow-lg border overflow-hidden flex flex-col bg-white border-gray-200">
            <div className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50 border-gray-200">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="font-medium text-gray-800">AI Analysis</span>
              {!analysis && !analysisLoading && (
                <Button onClick={handleAnalyze} size="sm" className="ml-auto h-7 text-xs">
                  <Sparkles className="h-3 w-3 mr-1" /> Analyze
                </Button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {analysisLoading && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
                    <p className="mt-3 text-sm animate-pulse text-gray-600">
                      {LOADING_MESSAGES[loadingMsgIndex]}
                    </p>
                  </div>
                </div>
              )}
              {!analysis && !analysisLoading && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Sparkles className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">Click Analyze to get AI insights</p>
                  </div>
                </div>
              )}
              {analysis && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="p-4 rounded-lg border bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <h3 className="font-semibold text-sm text-gray-800">AI Summary</h3>
                    </div>
                    <p className="text-sm leading-relaxed text-gray-600">{analysis.summary}</p>
                  </div>

                  {/* Metrics Grid */}
                  {analysis.sentiment.metrics && (
                    <div className="grid grid-cols-4 gap-2">
                      {METRICS.map(({ key, label, invertColor }) => {
                        const metric = analysis.sentiment.metrics[key]
                        const score = typeof metric === 'object' && 'score' in metric ? metric.score : 0
                        return (
                          <div key={key} className={`p-2 rounded border ${getScoreColor(score, invertColor)}`}>
                            <div className="text-[10px] truncate text-gray-500">{label}</div>
                            <div className={`text-lg font-bold ${getScoreTextColor(score, invertColor)}`}>{score}</div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Escalation */}
                  {analysis.sentiment.escalation && (
                    <div className={`p-3 rounded-lg border ${analysis.sentiment.escalation.recommended ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2">
                        <TrendingUp className={`h-4 w-4 ${analysis.sentiment.escalation.recommended ? 'text-red-500' : 'text-gray-500'}`} />
                        <span className="font-medium text-sm text-gray-800">
                          Escalation: {analysis.sentiment.escalation.recommended ? 'Recommended' : 'Not needed'}
                        </span>
                      </div>
                      {analysis.sentiment.escalation.factors && analysis.sentiment.escalation.factors.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {analysis.sentiment.escalation.factors.map((factor, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">{factor}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Suggested Actions */}
                  {analysis.suggestedActions && analysis.suggestedActions.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium mb-2 text-gray-500">Suggested Actions</h4>
                      <ul className="space-y-1">
                        {analysis.suggestedActions.map((action, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Actions Bar */}
        <div className="rounded-lg shadow-lg border p-3 bg-white border-gray-200">
          <div className="flex items-center gap-4">
            {/* Note Input */}
            <div className="flex gap-2 flex-1">
              <textarea
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                placeholder="Add note..."
                className="flex-1 px-3 py-2 text-sm border rounded-lg resize-none bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                rows={1}
              />
              <select
                value={noteType}
                onChange={(e) => setNoteType(e.target.value as 'private' | 'public')}
                className="text-xs border rounded-lg px-2 bg-white border-gray-300"
              >
                <option value="private">Private</option>
                <option value="public">Public</option>
              </select>
              <Button size="sm" onClick={handleSubmitNote} disabled={noteLoading || !noteInput.trim()}>
                {noteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  noteSuccess ? <CheckCircle className="w-4 h-4" /> :
                  <FileText className="w-4 h-4" />}
              </Button>
            </div>

            {/* Divider */}
            <div className="h-8 w-px bg-gray-200" />

            {/* Time Entry */}
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-gray-500" />
              {[15, 30, 60].map(mins => (
                <Button
                  key={mins}
                  variant={timeMinutes === mins ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs px-2 h-8"
                  onClick={() => setTimeMinutes(mins)}
                >
                  {mins}m
                </Button>
              ))}
              <Button size="sm" onClick={handleLogTime} disabled={timeLoading || timeMinutes === 0} className="h-8">
                {timeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  timeSuccess ? <CheckCircle className="w-4 h-4" /> :
                  'Log'}
              </Button>
            </div>

            {/* Divider */}
            <div className="h-8 w-px bg-gray-200" />

            {/* Response Generator */}
            <div className="flex items-center gap-1">
              <select
                value={responseTone}
                onChange={(e) => setResponseTone(e.target.value as 'professional' | 'friendly' | 'technical')}
                className="text-xs border rounded-lg px-2 py-1.5 bg-white border-gray-300"
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="technical">Technical</option>
              </select>
              <Button size="sm" onClick={handleGenerateResponse} disabled={responseLoading} className="h-8">
                {responseLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1" />Generate</>}
              </Button>
            </div>
          </div>

          {/* Generated Response */}
          {generatedResponse && (
            <div className="mt-3 p-3 rounded-lg border bg-gray-50 border-gray-200">
              <p className="text-sm whitespace-pre-wrap max-h-24 overflow-y-auto text-gray-700">{generatedResponse}</p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={handleCopyResponse}>
                  {copySuccess ? <CheckCircle className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  Copy
                </Button>
                <Button size="sm" variant="outline" onClick={handleUseAsNote}>
                  <FileText className="w-4 h-4 mr-1" />
                  Use as Note
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setGeneratedResponse('')}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
